import React, { useEffect } from 'react';
import { useCookieConsent } from '../context/CookieContext';

const CLARITY_ID = 'v1zhit6c14';

const ScriptLoader: React.FC = () => {
  const { consent, isConsentSet } = useCookieConsent();

  useEffect(() => {
    const w = window as any;
    const hasTrackingConsent = isConsentSet && (consent.analytics || consent.marketing);

    // Prepare GTM only after explicit analytics/marketing consent.
    if (hasTrackingConsent) {
      // Ensure dataLayer exists
      w.dataLayer = w.dataLayer || [];

      // Ensure gtag helper exists (so we can push consent mode commands into dataLayer)
      w.gtag =
        w.gtag ||
        function gtag(...args: any[]) {
          w.dataLayer.push(args);
        };

      // Default: everything denied (set only once)
      if (!w.__lucidConsentDefaultSet) {
        w.gtag('consent', 'default', {
          ad_storage: 'denied',
          analytics_storage: 'denied',
          // For Ads remarketing / user data signals (Consent Mode v2)
          ad_user_data: 'denied',
          ad_personalization: 'denied',
          wait_for_update: 500,
        });
        w.__lucidConsentDefaultSet = true;
      }

      // User choice in the banner
      const marketingGranted = consent.marketing ? 'granted' : 'denied';
      const analyticsGranted = consent.analytics ? 'granted' : 'denied';

      w.gtag('consent', 'update', {
        ad_storage: marketingGranted,
        analytics_storage: analyticsGranted,
        ad_user_data: marketingGranted,
        ad_personalization: marketingGranted,
      });

      // Load GTM only once
      if (!w.__gtmLoaded) {
        // Mirror standard GTM snippet: push gtm.js event so built-in triggers fire
        w.dataLayer.push({
          'gtm.start': new Date().getTime(),
          event: 'gtm.js',
        });

        const gtmScript = document.createElement('script');
        gtmScript.async = true;
        gtmScript.src = 'https://www.googletagmanager.com/gtm.js?id=GTM-M88N8QLF';
        document.head.appendChild(gtmScript);

        w.__gtmLoaded = true;
      }
    }

    // -----------------------------
    // Microsoft Clarity (GDPR-safe)
    // -----------------------------

    // Ensure clarity queue function exists (safe to call before script loads)
    w.clarity =
      w.clarity ||
      function clarity(...args: any[]) {
        (w.clarity.q = w.clarity.q || []).push(args);
      };

    // Always communicate consent state to Clarity once consent is set.
    // Clarity ConsentV2 expects ad_Storage + analytics_Storage as granted/denied.
    if (isConsentSet) {
      w.clarity('consentv2', {
        ad_Storage: consent.marketing ? 'granted' : 'denied',
        analytics_Storage: consent.analytics ? 'granted' : 'denied',
      });
    }

    // Load Clarity only after analytics consent (and only once)
    if (isConsentSet && consent.analytics && !w.__clarityLoaded) {
      const clarityScript = document.createElement('script');
      clarityScript.async = true;
      clarityScript.src = `https://www.clarity.ms/tag/${CLARITY_ID}`;
      document.head.appendChild(clarityScript);
      w.__clarityLoaded = true;
    }
  }, [consent.analytics, consent.marketing, isConsentSet]);

  return null;
};

export default ScriptLoader;
